import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from './authService';

export const clientInvoiceApi = {
  list: async (params = {}) => {
    const response = await api.get('/client/invoices', { params });
    return response.data;
  },
  getPendingSummary: async () => {
    const response = await api.get('/client/invoices/pending-summary');
    return response.data;
  },
  getOne: async (id) => {
    const response = await api.get(`/client/invoices/${id}`);
    return response.data;
  },
  accept: async (id, payload = {}) => {
    const response = await api.post(`/client/invoices/${id}/accept`, payload);
    return response.data;
  },
  reject: async (id, payload = {}) => {
    const response = await api.post(`/client/invoices/${id}/reject`, payload);
    return response.data;
  },
  markDownloaded: async (id) => {
    const response = await api.post(`/client/invoices/${id}/mark-downloaded`);
    return response.data;
  },
};

export const useClientInvoices = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['client-invoices', params],
    queryFn: () => clientInvoiceApi.list(params),
    staleTime: 30_000,
    ...options,
  });

export const useClientPendingInvoices = (options = {}) =>
  useQuery({
    queryKey: ['client-invoices-pending'],
    queryFn: () => clientInvoiceApi.getPendingSummary(),
    staleTime: 60_000,
    ...options,
  });

export const useClientInvoice = (id, options = {}) =>
  useQuery({
    queryKey: ['client-invoice', id],
    queryFn: () => clientInvoiceApi.getOne(id),
    enabled: Boolean(id),
    staleTime: 30_000,
    ...options,
  });

export const useAcceptClientInvoice = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) => clientInvoiceApi.accept(id, payload),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['client-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['client-invoices-pending'] });
      queryClient.invalidateQueries({ queryKey: ['clientBookings'] });
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};

export const useRejectClientInvoice = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) => clientInvoiceApi.reject(id, payload),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['client-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['client-invoices-pending'] });
      options.onSuccess?.(...args);
    },
    onError: options.onError,
  });
};
